import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { axiosInstance } from "../../utils/axiosInstance";
import { RootState } from "../store";

export interface Feature {
  disabled: boolean;
  id: number;
  featureName: string;
  featureDescription: string;
  isChecked: boolean;
}

export interface FeatureGroup {
  groupId: number;
  groupName: string;
  groupDescription: string;
  features: Feature[];
}

export interface FeatureGroupState {
  data: FeatureGroup[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  currentStep?: number;
}

const initialState: FeatureGroupState = {
  data: [],
  status: "idle",
  error: null,
  currentStep: 2,
};

// Thunk to fetch feature group data
export const fetchFeatureGroupWithFeatureData = createAsyncThunk(
  "featureGroupWithFeature/fetchData",
  async (selectedDomain: {
    app_id: number[];
    value_stream_id: number[];
    userData: any[];
  }) => {
    const response = await axiosInstance.post(
      "/featureGroupWithFeature/domain",
      {
        app_id: selectedDomain.app_id,
        value_stream_id: selectedDomain.value_stream_id,
        userData: selectedDomain?.userData,
      },
    );
    return response.data.data;
  },
);

const featureGroupSlice: any = createSlice({
  name: "featureGroupWithFeature",
  initialState,
  reducers: {
    updateFeatureSelection: (
      state,
      action: PayloadAction<{ groupId: any; featureId: any }>,
    ) => {
      const { groupId, featureId } = action.payload;

      // Find the group by groupId
      const group = state?.data?.find((group) => group.groupId === groupId);
      if (group) {
        // Find the feature in that group by featureId
        const feature = group?.features?.find(
          (feature) => feature.id === featureId,
        );

        if (feature) {
          // Update the isChecked status of the feature
          feature.isChecked = !feature.isChecked;
        }
      }
    },
    updateMultipleFeatureSelection: (
      state,
      action: PayloadAction<{ groupId: any; featureId: any }[]>, // Accept an array of updates
    ) => {
      const updates = action.payload; // Get the array of updates

      updates.forEach(({ groupId, featureId }) => {
        // Find the group by groupId
        const group = state?.data?.find((group) => group.groupId === groupId);
        if (group) {
          // Find the feature in that group by featureId
          const feature = group?.features?.find(
            (feature) => feature.id === featureId,
          );

          if (feature) {
            // Update the isChecked status of the feature
            feature.isChecked = !feature.isChecked;
          }
        }
      });
    },
    updateGroupselection: (
      state,
      action: PayloadAction<{ groupId: any; isGroupChecked: boolean }>,
    ) => {
      const { groupId, isGroupChecked } = action.payload;
      // Find the group by groupId
      const group = state?.data?.find((group) => group.groupId === groupId);

      if (group) {
        // Update each feature's isChecked property to true
        group.features = group.features.map((feature) => {
          return {
            ...feature, // Copy the other properties of the feature
            isChecked: feature?.disabled ? feature?.isChecked : isGroupChecked, // Set isChecked to true
          };
        });
      }
    },
    updateCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    updateFeatureStatus: (state, action: any) => {
      state.status = action.payload;
    },
    clearFeatureSelection: (state) => {
      state.data.forEach((group) => {
        group.features.forEach((feature) => {
          feature.isChecked = false;
        });
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureGroupWithFeatureData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchFeatureGroupWithFeatureData.fulfilled,
        (state, action: PayloadAction<FeatureGroup[] | string>) => {
          state.status = "succeeded";
          if (typeof action.payload === "string") {
            state.data = [];
            state.error = action.payload;
          } else {
            // Merge the fetched data with the existing state
            state.data = action.payload.map((group) => {
              const existingGroup = state.data.find(
                (g) => g.groupId === group.groupId,
              );

              // If the group already exists, merge the features and preserve `isChecked`
              if (existingGroup) {
                // Merge the features in the group, preserving `isChecked` values
                return {
                  ...group,
                  features: group.features.map((feature) => {
                    const existingFeature = existingGroup.features.find(
                      (f) => f.id === feature.id,
                    );
                    if (existingFeature && !feature.disabled) {
                      // Merge features and preserve `isChecked`
                      return {
                        ...feature,
                        isChecked: existingFeature.isChecked,
                      };
                    } else {
                      // If feature is new, just return it with default isChecked (false)
                      return { ...feature, isChecked: false };
                    }
                  }),
                };
              } else {
                // If the group is new, return it with default `isChecked` values (false)
                return {
                  ...group,
                  features: group.features.map((feature) => ({
                    ...feature,
                    isChecked: false,
                  })),
                };
              }
            });
            state.error = null;
          }
        },
      )
      .addCase(fetchFeatureGroupWithFeatureData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to load data";
      });
  },
});
export const {
  updateFeatureSelection,
  updateMultipleFeatureSelection,
  updateGroupselection,
  clearFeatureSelection,
  updateCurrentStep,
  updateFeatureStatus,
} = featureGroupSlice.actions;
export const selectFeatureGroupsData = (state: RootState) =>
  state.featureGroupWithFeature.data;
export default featureGroupSlice.reducer;
